<script setup lang="ts">
import { computed } from 'vue';
import { useDashboardStats } from '@/composables/useDashboardStats';
import { useI18n } from 'vue-i18n';

const { healthPct, alertCount, taskCount, isPending } = useDashboardStats();
const { t } = useI18n({ useScope: 'global' });

const healthLabel = computed(() => {
  if (healthPct.value >= 80) return t('dashboard.hero.healthGood');
  if (healthPct.value >= 50) return t('dashboard.hero.healthOk');
  return t('dashboard.hero.healthBad');
});
const healthLabelClass = computed(() => {
  if (healthPct.value >= 80) return 'good';
  if (healthPct.value >= 50) return 'ok';
  return 'bad';
});
</script>

<template>
  <section class="kpis">
    <!-- Santé globale -->
    <div class="kpi">
      <div class="kpi__icon">
        <i class="pi pi-shield" />
      </div>
      <div class="kpi__body">
        <p class="kpi__label">{{ t('dashboard.kpis.healthLabel') }}</p>
        <h3 class="kpi__value" :class="{ 'kpi__value--loading': isPending }">
          {{ isPending ? '…' : `${healthPct}%` }}
          <span
            v-if="!isPending"
            class="kpi__chip"
            :class="`kpi__chip--${healthLabelClass}`"
            >{{ healthLabel }}</span
          >
        </h3>
        <p class="kpi__desc">{{ t('dashboard.hero.healthDesc') }}</p>
      </div>
    </div>

    <!-- Alertes actives -->
    <div class="kpi">
      <div class="kpi__icon kpi__icon--alert">
        <i class="pi pi-exclamation-triangle" />
      </div>
      <div class="kpi__body">
        <p class="kpi__label">{{ t('dashboard.kpis.alertsLabel') }}</p>
        <h3
          class="kpi__value kpi__value--alert"
          :class="{ 'kpi__value--loading': isPending }"
        >
          {{ isPending ? '…' : alertCount }}
        </h3>
        <p class="kpi__desc">
          {{
            alertCount === 0
              ? t('dashboard.hero.alertsNone')
              : t('dashboard.hero.alertsDesc', { n: alertCount })
          }}
        </p>
      </div>
    </div>

    <!-- Tâches en attente -->
    <div class="kpi">
      <div class="kpi__icon kpi__icon--task">
        <i class="pi pi-clipboard" />
      </div>
      <div class="kpi__body">
        <p class="kpi__label">{{ t('dashboard.kpis.tasksLabel') }}</p>
        <h3 class="kpi__value" :class="{ 'kpi__value--loading': isPending }">
          {{ isPending ? '…' : taskCount }}
        </h3>
        <p class="kpi__desc">
          {{ t('dashboard.hero.tasksDesc', { n: taskCount }) }}
        </p>
      </div>
    </div>
  </section>
</template>

<style scoped>
.kpis {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}
@media (min-width: 640px) {
  .kpis {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* liquid-glass card */
.kpi {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 8px 32px 0 rgba(11, 19, 32, 0.03);
  border-radius: 1.5rem;
  padding: 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  transition: box-shadow 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}
.kpi:hover {
  box-shadow: 0 20px 60px rgba(11, 19, 32, 0.1);
}

.kpi__icon {
  width: 4rem;
  height: 4rem;
  min-width: 4rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.375rem;
  flex-shrink: 0;
  background: rgba(255, 214, 10, 0.1);
  color: #0b1320;
  border: 1px solid rgba(255, 214, 10, 0.2);
}
.kpi__icon--alert {
  background: rgba(239, 68, 68, 0.06);
  border-color: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}
.kpi__icon--task {
  background: rgba(11, 19, 32, 0.04);
  border-color: rgba(11, 19, 32, 0.08);
  color: rgba(11, 19, 32, 0.5);
}

.kpi__body {
  flex: 1;
  min-width: 0;
}

.kpi__label {
  margin: 0 0 0.375rem;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgba(11, 19, 32, 0.4);
}

.kpi__value {
  margin: 0 0 0.25rem;
  font-size: 1.875rem;
  font-weight: 800;
  color: #0b1320;
  letter-spacing: -0.02em;
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.kpi__value--alert {
  color: #ef4444;
}
.kpi__value--loading {
  opacity: 0.2;
}

.kpi__chip {
  font-size: 0.625rem;
  font-weight: 700;
  padding: 0.175rem 0.5rem;
  border-radius: 0.3rem;
  text-transform: none;
  letter-spacing: 0;
}
.kpi__chip--good {
  color: #16a34a;
}
.kpi__chip--ok {
  color: #b45309;
}
.kpi__chip--bad {
  color: #dc2626;
}

.kpi__desc {
  margin: 0;
  font-size: 0.6875rem;
  color: rgba(11, 19, 32, 0.4);
  line-height: 1.5;
}
</style>

<style>
[data-theme='dark'] .kpi {
  background: rgba(20, 28, 41, 0.75);
  border-color: rgba(255, 255, 255, 0.07);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}
[data-theme='dark'] .kpi:hover {
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
}
[data-theme='dark'] .kpi__label {
  color: rgba(255, 255, 255, 0.35);
}
[data-theme='dark'] .kpi__value {
  color: #f1f5f9;
}
[data-theme='dark'] .kpi__desc {
  color: rgba(255, 255, 255, 0.3);
}
[data-theme='dark'] .kpi__icon {
  background: rgba(255, 214, 10, 0.08);
  border-color: rgba(255, 214, 10, 0.15);
  color: #ffd60a;
}
[data-theme='dark'] .kpi__icon--task {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.35);
}
</style>
